import { Money } from '@principle-theorem/accounting';
import {
  DepositAmountType,
  isDepositAmount,
  type IDepositAmount,
} from '@principle-theorem/principle-core/interfaces';

/**
 * Centralises the display and persistence-unit conversions for deposit amounts
 * so the dollars-versus-cents storage difference cannot leak into form code.
 */
export class DepositAmountConversion {
  /**
   * Returns nothing for an amount the guard no longer accepts, so a percentage
   * saved before they were withdrawn renders as an empty required field rather
   * than as its own value reinterpreted as cents.
   */
  static toFormAmount(amount: IDepositAmount): number | undefined {
    return isDepositAmount(amount)
      ? Money.fromCents(amount.amountCents)
      : undefined;
  }

  /** Converts a form's dollar value to the persisted cents representation. */
  static fromFormAmount(displayValue: number): IDepositAmount {
    return {
      type: DepositAmountType.Fixed,
      amountCents: Money.toCents(displayValue),
    };
  }

  /**
   * Converts an optional patient deposit value from its form-dollar unit to
   * persisted cents while retaining the existing unset semantics.
   */
  static centsFromDollars(dollars?: number): number | undefined {
    return dollars ? Money.toCents(dollars) : undefined;
  }

  /**
   * Converts an optional persisted patient deposit amount to the dollar value
   * expected by its form control while retaining the existing unset semantics.
   */
  static dollarsFromCents(cents?: number): number | undefined {
    return cents ? Money.fromCents(cents) : undefined;
  }
}
